// import { trpc } from '../common/trpc';
import { StyleProvider } from '@ant-design/cssinjs';
import { ConfigProvider, theme } from 'antd';
import { useLocalStorage } from '../hooks/use-local-storage';
import { createContext, useContext, useSyncExternalStore } from 'react';
import { useEffect } from 'react';
import { getIsDarkMode, subscribeIsDarkMode } from '../utils/handleColorFormat';
const { defaultAlgorithm, darkAlgorithm } = theme;

const ThemeContext = createContext<{ isDarkMode: boolean; setIsDarkMode: (isDarkMode: boolean) => void } | undefined>(
  undefined,
);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [isDarkMode, setIsDarkMode] = useLocalStorage('darkMode', false);

  useEffect(() => {
    // ✅ 클라이언트 사이드에서만 실행되도록 체크
    if (typeof window !== 'undefined') {
      if (isDarkMode) {
        document.documentElement.classList.add('dark');
        // color-scheme 속성 추가
        document.documentElement.style.colorScheme = 'dark';
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.style.colorScheme = 'light';
      }
    }
  }, [isDarkMode]);

  return (
    <ThemeContext.Provider value={{ isDarkMode, setIsDarkMode }}>
      <StyleProvider layer>
        <ConfigProvider
          theme={{
            inherit: true,
            hashed: false,
            algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
            token: {
              colorPrimary: '#f47515',
              colorBorder: isDarkMode ? '#666' : '#d9d9d9',
              colorBorderSecondary: isDarkMode ? '#666' : '#d9d9d9',
            },
          }}
        >
          {children}
        </ConfigProvider>
      </StyleProvider>
    </ThemeContext.Provider>
  );
};

// chart-tools가 라이브러리로 쓰일 때 소비자(engview 등)가 ThemeProvider로
// 감싸지 않아도 `<html class="dark">` 토글을 구독해 리액티브하게 반영한다.
// MutationObserver·캐시 상태는 utils/handleColorFormat.ts의 공유 싱글톤을 재사용 —
// 앱 전체에서 observer 인스턴스가 최대 1개이며 getIsDarkMode()는 캐시 읽기만 한다.
export const useTheme = () => {
  const context = useContext(ThemeContext);
  // hook rules: 조건 없이 항상 호출. context가 존재하면 fallback 값은 사용되지 않는다.
  const fallbackIsDark = useSyncExternalStore(subscribeIsDarkMode, getIsDarkMode, () => false);
  if (context) return context;
  return { isDarkMode: fallbackIsDark, setIsDarkMode: () => {} };
};
